import { Backdrop, Box, Button, Typography } from '@mui/material'
import { memo, useCallback, useState } from 'react'
import Swal from 'sweetalert2'
import { getGazeInfo } from '../../../../../../axios/api/contest'
import CustomDialog from '../../../../../../components/dialog'
import Icon from '../../../../../../components/icon'
import { showErrorToast } from '../../../../../../utils/utils'
import GazeImageSwiper from './GazeImageSwiper'
import OverlayLoading from '../../../../../../components/overlay-loading'

const EvaluateGazeInfoModal = ({
  startTime,
  attemptId,
  onViewGazeTimeline
}) => {
  // State
  const [open, setOpen] = useState(false)
  const [gazeInfo, setGazeInfo] = useState([])
  const [loading, setLoading] = useState(false)
  const [previewState, setPreviewState] = useState({
    open: false,
    images: [],
    index: 0
  })

  // handlers
  const fetchGazeInfo = useCallback(async (attemptId) => {
    setLoading(true)
    const response = await getGazeInfo(attemptId)
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      setGazeInfo(response)
    }
  }, [])

  const handleOpen = useCallback(() => {
    setOpen(true)
    if (attemptId) {
      fetchGazeInfo(attemptId)
    }
  }, [fetchGazeInfo, attemptId])

  const handleClose = useCallback(() => {
    if (previewState.open) {
      setPreviewState({ open: false, images: [], index: 0 })
      return
    }
    if (Swal.isVisible()) {
      Swal.close()
      return
    }
    setOpen(false)
  }, [previewState.open])

  const handleViewOnTimeline = useCallback(
    async (fileName, timelineUrl) => {
      try {
        setLoading(true)
        const response = await fetch(timelineUrl)
        if (response.ok) {
          const data = await response.json()
          if (data && data.segments) {
            const start = new Date(startTime).getTime()
            const segmentStart = new Date(
              parseInt(fileName?.split('_')?.[1])
            ).getTime()
            const diff = startTime ? segmentStart - start : 0

            onViewGazeTimeline(
              data?.segments?.map(
                (item) =>
                  ({
                    ...item,
                    start_ms: item?.start_ms + diff,
                    end_ms: item?.end_ms + diff
                  }) || []
              )
            )
            handleClose()
          }
        } else {
          throw new Error('Failed to fetch timeline data')
        }
      } catch (error) {
        showErrorToast({ message: 'Failed to fetch timeline data' })
      } finally {
        setLoading(false)
      }
    },
    [handleClose, startTime, onViewGazeTimeline]
  )

  const handleImageClick = useCallback((index, images) => {
    setPreviewState({ open: true, images, index })
  }, [])

  const handlePreviewClose = useCallback(() => {
    setPreviewState({ open: false, images: [], index: 0 })
  }, [])

  const handlePreviewPrev = useCallback((e) => {
    e.stopPropagation()
    setPreviewState((prev) => ({ ...prev, index: Math.max(0, prev.index - 1) }))
  }, [])

  const handlePreviewNext = useCallback((e) => {
    e.stopPropagation()
    setPreviewState((prev) => ({
      ...prev,
      index: Math.min(prev.images.length - 1, prev.index + 1)
    }))
  }, [])

  return (
    <>
      <Button
        variant="contained"
        className="editor-btn"
        onClick={handleOpen}
        startIcon={<Icon icon="mdi:eye-outline" style={{ fontSize: '14px' }} />}
        sx={{ padding: '5px 10px', fontSize: '12px' }}
      >
        View Gaze Info
      </Button>

      <CustomDialog
        open={open}
        handleClose={handleClose}
        title="Gaze Info"
        dialogPaperStyle={{ maxWidth: '900px', width: '100%' }}
      >
        {loading && <OverlayLoading />}
        {!loading && gazeInfo.length === 0 && (
          <Typography>No gaze info available.</Typography>
        )}

        {gazeInfo.map((webcam, webcamIndex) => (
          <Box key={webcam.name} sx={{ mb: 4 }}>
            {gazeInfo?.length > 1 && (
              <Typography variant="h6" sx={{ mb: 2 }}>
                {`Video ${webcamIndex + 1}`}
              </Typography>
            )}
            {webcam.values.map((cluster, clusterIndex) => {
              const timelineFile = cluster.values.find(
                (f) => f.fileName === 'timeline.json'
              )
              const images = cluster.values.filter(
                (f) => f.fileName !== 'timeline.json'
              )

              return (
                <Box
                  key={cluster.name}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid #eee',
                    borderRadius: 2
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {`Gaze Direction ${clusterIndex + 1}`}
                    </Typography>
                    {timelineFile && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Icon icon="mdi:chart-timeline-variant" />}
                        onClick={() =>
                          handleViewOnTimeline(webcam?.name, timelineFile.url)
                        }
                      >
                        Show on Timeline
                      </Button>
                    )}
                  </Box>
                  <GazeImageSwiper
                    images={images}
                    onImageClick={(index) => handleImageClick(index, images)}
                  />
                </Box>
              )
            })}
          </Box>
        ))}
      </CustomDialog>

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 10,
          display: 'flex',
          flexDirection: 'column'
        }}
        open={previewState.open}
        onClick={handlePreviewClose}
      >
        {previewState.open && previewState.images.length > 0 && (
          <>
            <Icon
              icon="mdi:close"
              fontSize={30}
              hoverEffect
              onClick={handlePreviewClose}
              hoverBoxStyle={{
                position: 'absolute',
                top: 20,
                right: 20,
                color: 'var(--common-white)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                borderRadius: '50%',
                zIndex: 1
              }}
            />
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '90vw',
                maxHeight: '90vh',
                userSelect: 'none'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Icon
                icon="mdi:chevron-left"
                fontSize={40}
                hoverEffect
                disabled={previewState.index === 0}
                onClick={handlePreviewPrev}
                hoverBoxStyle={{
                  position: 'absolute',
                  left: 10,
                  color: 'var(--common-white)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: '50%',
                  padding: '8px'
                }}
              />

              <img
                src={previewState.images[previewState.index]?.url}
                alt="preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  display: 'block',
                  userSelect: 'none'
                }}
              />

              <Icon
                icon="mdi:chevron-right"
                fontSize={40}
                hoverEffect
                disabled={previewState.index === previewState.images.length - 1}
                onClick={handlePreviewNext}
                hoverBoxStyle={{
                  position: 'absolute',
                  right: 10,
                  color: 'var(--common-white)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: '50%',
                  padding: '8px'
                }}
              />
            </Box>
          </>
        )}
      </Backdrop>
    </>
  )
}

export default memo(EvaluateGazeInfoModal)
